import React, { useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { AuthenticatedRoute } from "packable-components";
import { logDebug, logInfo } from "packable-components";

// Packble Storage Client application framework page
import PackableStorageClientPage from "./framework/PackableStorageClientPage";

import { config } from "../config";
import {
  AboutPackablePage,
  AboutPage,
  ActivationConfirmationPage,
  CategoriesPage,
  ClientSystemInfoPage,
  ContactUsProtectedPage,
  ContactUsUnprotectedPage,
  EntityType,
  IPackablePageContent,
  InitiatePasswordResetPage,
  InventoryPage,
  LoginPage,
  LogoutPage,
  PageNotFound,
  PasswordResetConfirmationPage,
  PasswordResetPage,
  PaymentPage,
  PlanPage,
  PromotionPage,
  RegistrationConfirmationPage,
  SettingsPage,
  SpacePage,
  SubCategoriesPage,
  SubscriptionPage,
  TagPage,
  UnprotectedPackablePage,
  UnprotectedPackablePageContent,
  UserAccount,
  UserRegistrationPage,
  UsersPage,
} from "packable-common";

import appLogoName from "../img/logo_name.png";
import appLogo from "../img/logo_login.png";
import appLogoSmall from "../img/logo_small.png";
import StorageItemPage from "./items/StorageItemPage";
import HomePage from "./home/HomePage";
import OrderPage from "./orders/OrderPage";

(window as any).logLevel = "DEBUG";
logInfo(`Initialized client with window.logLevel: ${(window as any).logLevel}`);

export interface MainProps {
  [x: string | number | symbol]: unknown;
}

export const Main: React.FC<any> = (props): React.JSX.Element => {
  logDebug("In Main with props:");
  logDebug(props);

  const [requestedURL, setRequestedURL] = useState("/");

  return (
    <BrowserRouter basename={(window as any).webBasedURL}>
      <Routes>
        <Route
          path="/about_packable"
          element={
            <UnprotectedPackablePage
              appLogo={appLogo}
              appLogoName={appLogoName}
              appLogoSmall={appLogoSmall}
              content={function (pageProps: UnprotectedPackablePageContent) {
                return <AboutPackablePage {...pageProps} />;
              }}
            />
          }
        />
        <Route
          path="/contact"
          element={
            <UnprotectedPackablePage
              appLogo={appLogo}
              appLogoName={appLogoName}
              appLogoSmall={appLogoSmall}
              content={function (pageProps: UnprotectedPackablePageContent) {
                return <ContactUsUnprotectedPage {...pageProps} />;
              }}
            />
          }
        />
        <Route
          path="/user/initpasswordreset"
          element={<InitiatePasswordResetPage appLogo={appLogo} />}
        />
        <Route
          path="/user/passwordresetconfirmation"
          element={<PasswordResetConfirmationPage appLogo={appLogo} />}
        />
        <Route
          path="/user/passwordreset/:token"
          element={<PasswordResetPage appLogo={appLogo} />}
        />
        <Route
          path="/user/registration/:promoCode/:userEmail"
          element={
            <UnprotectedPackablePage
              appLogo={appLogo}
              appLogoName={appLogoName}
              appLogoSmall={appLogoSmall}
              content={function (pageProps: UnprotectedPackablePageContent) {
                pageProps.entityType = EntityType.STORAGE_CLIENT;
                return <UserRegistrationPage {...pageProps} />;
              }}
            />
          }
        />
        <Route
          path="/user/registration/:promoCode"
          element={
            <UnprotectedPackablePage
              appLogo={appLogo}
              appLogoName={appLogoName}
              appLogoSmall={appLogoSmall}
              content={function (pageProps: UnprotectedPackablePageContent) {
                pageProps.entityType = EntityType.STORAGE_CLIENT;
                return <UserRegistrationPage {...pageProps} />;
              }}
            />
          }
        />
        <Route
          path="/user/registration"
          element={
            <UnprotectedPackablePage
              appLogo={appLogo}
              appLogoName={appLogoName}
              appLogoSmall={appLogoSmall}
              content={function (pageProps: UnprotectedPackablePageContent) {
                pageProps.entityType = EntityType.STORAGE_CLIENT;
                return <UserRegistrationPage {...pageProps} />;
              }}
            />
          }
        />
        <Route
          path="/user/registration_confirmation"
          element={
            <UnprotectedPackablePage
              appLogo={appLogo}
              appLogoName={appLogoName}
              appLogoSmall={appLogoSmall}
              content={function (pageProps: UnprotectedPackablePageContent) {
                return <RegistrationConfirmationPage {...pageProps} />;
              }}
            />
          }
        />
        <Route
          path="/user/complete_registration/:token"
          element={
            <UnprotectedPackablePage
              appLogo={appLogo}
              appLogoName={appLogoName}
              appLogoSmall={appLogoSmall}
              content={function (pageProps: UnprotectedPackablePageContent) {
                return <ActivationConfirmationPage {...pageProps} />;
              }}
            />
          }
        />
        <Route
          path="/login"
          element={
            <LoginPage apiToken={config.API_KEY} appLogo={appLogo} requestedURL={requestedURL} />
          }
        />
        <Route
          path="/logout"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={<LogoutPage appLogo={appLogo} />}
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/items"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="item"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <StorageItemPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/items/:searchString"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="item"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <StorageItemPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/items/:inventoryId/item/:itemId"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="item"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <HomePage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/spaces"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <SpacePage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/categories"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="category"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <CategoriesPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/subcategories/:categoryId/:categoryName"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="category"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <SubCategoriesPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/tags"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <TagPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/orders"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="order"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <OrderPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/payments"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="payment"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <PaymentPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/inventories"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <InventoryPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/inventories/:inventoryId"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <InventoryPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/about"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <AboutPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/account"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <UserAccount {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/subscription"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <SubscriptionPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/settings"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <SettingsPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/users"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="packable_user"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <UsersPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/subscription_plans"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="system"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <PlanPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/promotions"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="system"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <PromotionPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/clients"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="system"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <ClientSystemInfoPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/user/contact"
          element={
            <AuthenticatedRoute
              path={window.location.href}
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <ContactUsProtectedPage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route
          path="/"
          element={
            <AuthenticatedRoute
              path="/"
              object="application"
              unauthorizedURL="/unauthorized"
              unauthenticatedURL="/login"
              element={
                <PackableStorageClientPage
                  appLogo={appLogo}
                  appLogoName={appLogoName}
                  appLogoSmall={appLogoSmall}
                  content={function (pageProps: IPackablePageContent) {
                    return <HomePage {...pageProps} />;
                  }}
                />
              }
              redirectHandler={setRequestedURL}
            />
          }
        />
        <Route path="*" element={<PageNotFound appLogo={appLogo} />} />
      </Routes>
    </BrowserRouter>
  );
};

export default Main;
